import type { MapPlace, MapPoint } from './mapModel';
const n = (v: number): string => v.toFixed(1);
const path = (d: string, fill = 'none', extra = ''): string => `<path d="${d}" fill="${fill}" ${extra}/>`;

export function riverLine(points: MapPoint[]): string {
  let d = `M${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length - 1; i++) {
    const a = points[i-1], b = points[i], c = points[i+1], before = Math.hypot(b.x-a.x,b.y-a.y), after = Math.hypot(c.x-b.x,c.y-b.y);
    const r = Math.min(12,before/3,after/3);
    if (!before || !after) continue;
    d += `L${n(b.x-(b.x-a.x)/before*r)} ${n(b.y-(b.y-a.y)/before*r)}Q${b.x} ${b.y} ${n(b.x+(c.x-b.x)/after*r)} ${n(b.y+(c.y-b.y)/after*r)}`;
  }
  const end = points.at(-1)!; return d + `L${end.x} ${end.y}`;
}

/** Sample rounded bends, then draw uneven banks around the stream rather than a stroked pipe. */
export function riverShape(points: MapPoint[], bank = 0): string {
  const samples: MapPoint[] = [points[0]];
  const line = (to: MapPoint): void => {
    const from = samples.at(-1)!, count = Math.max(1,Math.ceil(Math.hypot(to.x-from.x,to.y-from.y)/11));
    for(let i=1;i<=count;i++)samples.push({x:from.x+(to.x-from.x)*i/count,y:from.y+(to.y-from.y)*i/count});
  };
  for(let i=1;i<points.length-1;i++){
    const a=points[i-1],b=points[i],c=points[i+1],before=Math.hypot(b.x-a.x,b.y-a.y),after=Math.hypot(c.x-b.x,c.y-b.y);
    if(!before||!after)continue;
    const radius=Math.min(28,before*.4,after*.4),enter={x:b.x-(b.x-a.x)/before*radius,y:b.y-(b.y-a.y)/before*radius},exit={x:b.x+(c.x-b.x)/after*radius,y:b.y+(c.y-b.y)/after*radius};
    line(enter);
    for(let k=1;k<=8;k++){const t=k/8,u=1-t;samples.push({x:u*u*enter.x+2*u*t*b.x+t*t*exit.x,y:u*u*enter.y+2*u*t*b.y+t*t*exit.y});}
  }
  line(points.at(-1)!);
  const left:MapPoint[]=[],right:MapPoint[]=[];
  for(let i=0;i<samples.length;i++){
    const p=samples[i],a=samples[Math.max(0,i-1)],b=samples[Math.min(samples.length-1,i+1)],len=Math.hypot(b.x-a.x,b.y-a.y)||1;
    const w=7.5+Math.sin(p.x*.055+p.y*.03)*1.5+Math.sin(p.y*.11)*.9+bank,nx=-(b.y-a.y)/len,ny=(b.x-a.x)/len;
    left.push({x:p.x+nx*w,y:p.y+ny*w});right.push({x:p.x-nx*w,y:p.y-ny*w});
  }
  return 'M'+[...left,...right.reverse()].map(p=>`${n(p.x)} ${n(p.y)}`).join('L')+'Z';
}

export function roomArt(p: MapPlace): string {
  const current=p.state==='current', wall = current?'#b48656':'#969b85', floor = current?'#efdaa2':'#e1d7b6', edge=current?'#995b40':'#777c68';
  let shell = '', masonry = '';
  if (p.id === 'ferry') {
    shell = '<ellipse cy="-30" rx="68" ry="58" fill="#b3b7a1" stroke="'+edge+'" stroke-width="4"/><ellipse cy="-30" rx="60" ry="50" fill="' + floor + '" stroke="'+wall+'"/>';
    masonry = Array.from({ length: 10 }, (_, i) => { const a = i/10*Math.PI*2; return path(`M${n(Math.cos(a)*61)} ${n(-30+Math.sin(a)*51)}L${n(Math.cos(a)*68)} ${n(-30+Math.sin(a)*58)}`); }).join('');
  } else {
    let outer: string, inner: string;
    if (p.id === 'scribe') {
      outer = 'M-73 24V-84L-62-94H61L73-84V24Z'; inner = 'M-65 16V-80L-57-86H57L65-80V16Z';
      masonry = path('M-44-94v9M-14-94v9M16-94v9M46-94v9M-73-60h8m-8 30h8M65-60h8m-8 30h8');
    } else if (p.id === 'chapel') {
      outer = 'M-55 25V-60Q-55-108 0-110Q55-108 55-60V25Z'; inner = 'M-47 17V-60Q-47-100 0-102Q47-100 47-60V17Z';
      masonry = path('M0-109v8M-29-102l3 8m-26 33l8 3M29-102l-3 8m26 33l-8 3M-55-24h8m94 0h8');
    } else if (p.id === 'knight') {
      outer = 'M-53 28V-81L-38-96H38L53-81V28Z'; inner = 'M-45 20V-77L-34-88H34L45-77V20Z';
      masonry = path('M-16-96v8m31-8v8M-53-52h8m-8 30h8M45-52h8m-8 30h8');
    } else if (p.id === 'garden') {
      outer = 'M-65 22L-70-20-65-73-45-90H44L65-72 68-24 61 24Z'; inner = 'M-57 14L-62-20-57-69-41-82H40L57-68 60-24 54 16Z';
      masonry = path('M-36-90l2 9m36-9v8m34-8l-3 8M-69-33l8 2m121-2l8-2');
    } else {
      outer = 'M-64 23V-69Q-64-92-39-92H40Q65-92 65-69V23Z'; inner = 'M-56 15V-68Q-56-84-38-84H39Q57-84 57-68V15Z';
      masonry = path('M-30-92v8m30-8v8m30-8v8M-64-54h8m-8 32h8M57-54h8m-8 32h8');
    }
    shell = path(outer,'#b3b7a1',`stroke="${edge}" stroke-width="4"`) + path(inner,floor,`stroke="${wall}"`);
  }
  // The open threshold joins the room to the path, instead of sealing it in a card.
  shell += path('M-12 23H12','none',`stroke="${floor}" stroke-width="7"`) + path('M-12 18v10m24-10v10','none',`stroke="${wall}" stroke-width="2"`);
  if (p.id !== 'chapel' && p.id !== 'garden') {
    const side = p.id === 'knight' ? 37 : -48;
    shell += `<ellipse cx="${side}" cy="-43" rx="9" ry="17" fill="#d2ad59" opacity=".2"/>`
      + path(`M${side} -34v-15m-3 15h6`,'none','stroke="#957f4b" stroke-width="2"')
      + path(`M${side} -49q-5-7 0-12q5 8 0 12`,'#c89540','stroke="none"');
  }
  return `<g transform="translate(${p.x} ${p.y})">${shell}<g stroke="${wall}" stroke-width="1.2">${masonry}</g></g>`;
}

export function distanceToSegment(p: MapPoint, a: MapPoint, b: MapPoint): number {
  const dx = b.x-a.x, dy = b.y-a.y, t = Math.max(0,Math.min(1,((p.x-a.x)*dx+(p.y-a.y)*dy)/(dx*dx+dy*dy||1)));
  return Math.hypot(p.x-a.x-dx*t,p.y-a.y-dy*t);
}
